// react
import { useState } from 'react';
import type { ReactNode } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Box, Popover, Stack, Tooltip, Typography, useTheme } from '@mui/material';

// utils
import { formatCurrency } from '../../../utils/currency';
import type { FxRate } from '../../../utils/fxRate';

// Types for different variants
interface BaseFinancialData {
	currency: string;
	planned: number;
	committed: number;
	spent: number;
	/**
	 * Local currency code for the parallel budget, when the activity/work plan
	 * carries one. When set alongside `fx`, each figure is also shown restated
	 * in this currency.
	 */
	currencyLocal?: string | null;
	/**
	 * Reporting currency code, used to label the cross-currency caption when
	 * the primary `currency` is itself the local currency. Without it the row
	 * has no way to surface a sensible second figure and the caption is
	 * omitted instead of showing the same currency twice.
	 */
	currencyReporting?: string | null;
	/**
	 * Canonical FX rate from one of the resolvers in `utils/fxRate.ts`. Carries
	 * direction explicitly via its reporting/local currency tags, so the row
	 * never has to guess which way to convert. When absent the caption is
	 * omitted instead of fabricating a figure.
	 */
	fx?: FxRate | null;
}

interface SubActivityAllocation {
	allocated: number;
	unallocated: number;
}

interface ContractFinancialData {
	currency: string;
	contractValue: number;
	commitmentAmount?: number | null;
	paid: number;
	/**
	 * Per-loan breakdown of the contract's REDEEMED voucher total, derived
	 * from `voucher.financingSources`. When present and contains more than
	 * one row, the Paid stat's tooltip switches from the generic blurb to a
	 * compact loan-by-loan table so users can see how the payments split
	 * across financing sources without leaving the contract page.
	 */
	paidByLoan?: { loanId: number; loanName?: string | null; amount: number }[];
	/**
	 * Legacy payments recorded against this contract under V1, before it was
	 * onboarded into V2. Reduces the remaining amount and is shown as its own
	 * segment/stat so it is not conflated with V2 paid.
	 */
	paidV1?: number;
	/**
	 * Pending vouchers — created but not yet redeemed (excludes CANCELLED).
	 * Shown as a separate segment in the progress bar and stat in the row.
	 * Does NOT count toward the headline paid % so the % remains driven by
	 * REDEEMED vouchers only.
	 */
	pending?: number;
	/**
	 * Additional contract amounts in currencies other than `currency`.
	 * Rendered as a small caption so a multi-currency contract is not
	 * collapsed into a single figure under a single label.
	 */
	additionalAmounts?: { currency: string; amount: number }[];
	/**
	 * The parent work plan's currency, when it differs from `currency`.
	 * Surfaced as a sub-label under the contract value so users see the
	 * relationship between the contract figure and the planning currency
	 * without one being silently rendered as the other.
	 */
	workPlanCurrency?: string;
	/**
	 * Local currency code for the parallel budget. When set alongside
	 * `fx`, each contract figure is also shown restated in this currency.
	 */
	currencyLocal?: string | null;
	/**
	 * Reporting currency code, used to label the cross-currency caption when
	 * the contract's primary `currency` is itself the local currency.
	 */
	currencyReporting?: string | null;
	/**
	 * Canonical FX rate from `resolveContractFxRate` in `utils/fxRate.ts`.
	 * Carries direction explicitly via its reporting/local currency tags and
	 * surfaces the rate's source via the `<LocalEquivalent>` tooltip. When
	 * absent the local caption is omitted instead of fabricating a figure.
	 */
	fx?: FxRate | null;
}

// Props for the component
interface RowFinancialSummaryProps {
	/** Financial data for work plan/activity variant */
	data?: BaseFinancialData;
	/** Financial data for contract variant */
	contractData?: ContractFinancialData;
	/** Sub-activity allocation data (only for activity variant) */
	subActivityAllocation?: SubActivityAllocation;
	/** The type of summary to display */
	variant: 'workPlan' | 'activity' | 'contract';
	/** Optional: Show compact version (even more condensed) */
	compact?: boolean;
}

// Tooltip explanations for financial terms
const TOOLTIPS = {
	allocated: 'Total amount distributed to sub-activities within this activity',
	available: 'Remaining budget that can be allocated to new commitments',
	commitmentAmount:
		'The committed portion of the contract value that has been reserved for disbursement',
	committed: 'Budget reserved for contracts and pending payments',
	contractValue: 'Total value of the contract as agreed with the supplier',
	paid: 'Actual payments made to the supplier',
	paidV1:
		'Legacy payments recorded against this contract under V1, before it was onboarded into V2',
	pending: 'Vouchers created but not yet redeemed (does not count toward the paid percentage).',
	planned: 'Total budget allocation for this item',
	plannedWorkPlan: 'Budget allocated to activities within this work plan',
	remaining: 'Amount still to be paid under this contract',
	spent: 'Actual disbursements that have been processed and redeemed',
	unallocated: 'Planned amount not yet assigned to sub-activities',
	utilization: 'Percentage of planned budget that has been committed or spent',
	workPlanAmount: 'Total budget allocation for this work plan',
};

// Stat item component matching HeaderDetailPage stats styling
const StatItem = ({
	color,
	label,
	secondary,
	tooltip,
	value,
}: {
	color?: string;
	label: string;
	/** Optional local-currency restatement shown as a smaller caption. */
	secondary?: string;
	// ReactNode so callers can pass a rich breakdown (e.g. the per-loan
	// split for a multi-source Paid figure) instead of just a plain string.
	tooltip: ReactNode;
	value: string;
}) => (
	<Tooltip title={tooltip} arrow placement="top">
		<Box sx={{ cursor: 'help' }}>
			<Typography
				sx={{
					color: '#6b7280',
					fontSize: { xs: '1.125rem', sm: '1.25rem' },
					fontWeight: 600,
					letterSpacing: '0.03em',
					mb: 0.25,
					textTransform: 'uppercase',
				}}>
				{label}
			</Typography>
			<Typography
				sx={{
					color: color || '#111827',
					fontSize: { xs: '1.3125rem', sm: '1.5rem' },
					fontWeight: 600,
				}}>
				{value}
			</Typography>
			{secondary && (
				<Typography
					sx={{
						color: '#6b7280',
						fontSize: { xs: '1.0625rem', sm: '1.15rem' },
						fontWeight: 500,
						mt: 0.25,
					}}>
					{secondary}
				</Typography>
			)}
		</Box>
	</Tooltip>
);

// Progress bar segment
const ProgressSegment = ({
	color,
	onMouseEnter,
	onMouseLeave,
	width,
}: {
	color: string;
	onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
	onMouseLeave: () => void;
	width: string;
}) => (
	<Box
		onMouseEnter={onMouseEnter}
		onMouseLeave={onMouseLeave}
		sx={{
			bgcolor: color,
			height: '100%',
			minWidth: width !== '0%' ? 2 : 0,
			transition: 'all 0.3s ease',
			width: width,
			'&:hover': {
				filter: 'brightness(1.1)',
			},
		}}
	/>
);

export const RowFinancialSummary = ({
	compact = false,
	contractData,
	data,
	subActivityAllocation,
	variant,
}: RowFinancialSummaryProps) => {
	const theme = useTheme();
	const { t } = useTranslation();

	// Popover state
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [popoverContent, setPopoverContent] = useState<{
		color: string;
		label: string;
		value: string;
	} | null>(null);

	const handlePopoverOpen = (
		event: React.MouseEvent<HTMLElement>,
		content: { color: string; label: string; value: string }
	) => {
		setAnchorEl(event.currentTarget);
		setPopoverContent(content);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
		setPopoverContent(null);
	};

	const popoverOpen = Boolean(anchorEl);

	// Calculate derived values for work plan/activity variants
	const availableAmount = data ? data.planned - data.committed : 0;
	const spentPercent = data && data.planned > 0 ? Math.round((data.spent / data.planned) * 100) : 0;

	// Restate the headline figure in the *other* of the two project currencies
	// using the resolved FX rate. When the row's primary `currency` is the
	// reporting currency we show the local equivalent; when it is already the
	// local currency we show the reporting equivalent instead, so the caption
	// never repeats the same currency back at the user. Returns undefined when
	// no rate is available or the row carries only one currency, in which case
	// the caption is simply omitted.
	const localCaption = (value: number): string | undefined => {
		if (!data?.fx) return undefined;
		const { rate, reportingCurrency, localCurrency } = data.fx;
		if (data.currency === reportingCurrency && reportingCurrency !== localCurrency) {
			return formatCurrency({ currency: localCurrency, value: value * rate });
		}
		if (data.currency === localCurrency && reportingCurrency !== localCurrency) {
			return formatCurrency({ currency: reportingCurrency, value: value / rate });
		}
		return undefined;
	};
	const commitmentPercent =
		data && data.planned > 0 ? Math.round((data.committed / data.planned) * 100) : 0;
	// Total budget engagement = committed + spent as % of planned (capped at 100)
	const engagementPercent = Math.min(100, commitmentPercent + spentPercent);
	// Use engagement percent for the headline utilization display
	const utilizationPercent = engagementPercent;

	// Calculate derived values for contract variant
	const effectiveContractAmount = contractData?.contractValue ?? 0;
	const hasCommitment =
		contractData?.commitmentAmount != null &&
		contractData.commitmentAmount !== effectiveContractAmount;
	const commitmentAmount = contractData?.commitmentAmount ?? effectiveContractAmount;
	const paidPercent =
		commitmentAmount > 0 ? Math.round(((contractData?.paid ?? 0) / commitmentAmount) * 100) : 0;
	const paidV1Amount = contractData?.paidV1 ?? 0;
	// Legacy V1 paid fills the bar after V2 paid; clamp so the combined paid
	// segments never exceed 100%.
	const paidV1Percent =
		commitmentAmount > 0
			? Math.max(
					0,
					Math.min(100 - paidPercent, Math.round((paidV1Amount / commitmentAmount) * 100))
				)
			: 0;
	const pendingAmount = contractData?.pending ?? 0;
	// Pending fills the bar after paid; clamp so paid + pending never exceed 100%.
	const pendingPercent =
		commitmentAmount > 0
			? Math.max(
					0,
					Math.min(
						100 - paidPercent - paidV1Percent,
						Math.round((pendingAmount / commitmentAmount) * 100)
					)
				)
			: 0;
	const remainingPercent = Math.max(0, 100 - paidPercent - paidV1Percent - pendingPercent);
	const remainingAmount = contractData
		? Math.max(0, commitmentAmount - contractData.paid - paidV1Amount - pendingAmount)
		: 0;

	// Restate the headline contract figure in the *other* of the two project
	// currencies using the resolved contract FX rate. When the contract is
	// already denominated in the local currency we show the reporting equivalent
	// instead, so the caption never repeats the same currency back at the user
	// (which previously rendered, e.g., `ETB 100,000.00 / ETB 17,000,000.00`
	// for an ETB contract on a USD-reporting project). Returns undefined when
	// no rate is available or the row carries only one distinct currency, in
	// which case the caption is simply omitted.
	const localCaptionContract = (value: number): string | undefined => {
		if (!contractData?.fx) return undefined;
		const { rate, reportingCurrency, localCurrency } = contractData.fx;
		if (contractData.currency === reportingCurrency && reportingCurrency !== localCurrency) {
			return formatCurrency({ currency: localCurrency, value: value * rate });
		}
		if (contractData.currency === localCurrency && reportingCurrency !== localCurrency) {
			return formatCurrency({ currency: reportingCurrency, value: value / rate });
		}
		return undefined;
	};

	// Render contract variant
	const renderContractRow = () => {
		if (!contractData) return null;

		return (
			<Stack
				direction="row"
				alignItems="center"
				spacing={{ xs: 2, md: 4 }}
				sx={{ flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
				{/* Progress bar with label - matching stat item layout */}
				<Box sx={{ minWidth: 280 }}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{ mb: 0.5 }}>
						<Typography
							sx={{
								color: '#6b7280',
								fontSize: { xs: '1.125rem', sm: '1.25rem' },
								fontWeight: 600,
								letterSpacing: '0.03em',
								textTransform: 'uppercase',
							}}>
							{t('paymentProgress')}
						</Typography>
						<Typography
							sx={{
								color: theme.palette.success.main,
								fontSize: { xs: '1.3125rem', sm: '1.5rem' },
								fontWeight: 600,
							}}>
							{paidPercent}%
						</Typography>
					</Stack>
					<Box
						sx={{
							bgcolor: alpha(theme.palette.grey[300], 0.4),
							borderRadius: 0.5,
							display: 'flex',
							height: 10,
							overflow: 'hidden',
						}}>
						<ProgressSegment
							color={theme.palette.success.main}
							onMouseEnter={(e) =>
								handlePopoverOpen(e, {
									color: 'success.main',
									label: 'Amount Paid',
									value: formatCurrency({
										currency: contractData.currency,
										value: contractData.paid,
									}),
								})
							}
							onMouseLeave={handlePopoverClose}
							width={`${paidPercent}%`}
						/>
						{paidV1Percent > 0 && (
							<ProgressSegment
								color={alpha(theme.palette.success.main, 0.45)}
								onMouseEnter={(e) =>
									handlePopoverOpen(e, {
										color: 'success.main',
										label: `Legacy (V1) — ${paidV1Percent}%`,
										value: formatCurrency({
											currency: contractData.currency,
											value: paidV1Amount,
										}),
									})
								}
								onMouseLeave={handlePopoverClose}
								width={`${paidV1Percent}%`}
							/>
						)}
						{pendingPercent > 0 && (
							<ProgressSegment
								color={alpha(theme.palette.warning.main, 0.6)}
								onMouseEnter={(e) =>
									handlePopoverOpen(e, {
										color: 'warning.main',
										label: `Pending — ${pendingPercent}%`,
										value: formatCurrency({
											currency: contractData.currency,
											value: pendingAmount,
										}),
									})
								}
								onMouseLeave={handlePopoverClose}
								width={`${pendingPercent}%`}
							/>
						)}
						<ProgressSegment
							color={alpha(theme.palette.info.main, 0.3)}
							onMouseEnter={(e) =>
								handlePopoverOpen(e, {
									color: 'info.main',
									label: 'Remaining',
									value: formatCurrency({
										currency: contractData.currency,
										value: remainingAmount,
									}),
								})
							}
							onMouseLeave={handlePopoverClose}
							width={`${remainingPercent}%`}
						/>
					</Box>
				</Box>

				{/* Stats */}
				<StatItem
					label={t('contractValue')}
					tooltip={TOOLTIPS.contractValue}
					value={formatCurrency({
						currency: contractData.currency,
						value: contractData.contractValue,
					})}
					secondary={localCaptionContract(contractData.contractValue)}
				/>
				{(() => {
					// Additional contract amounts are only shown for genuinely foreign
					// currencies. An amount already denominated in the local currency
					// is omitted here because every figure carries its local
					// equivalent as a caption, so repeating it would just duplicate
					// the number under a confusing "+" prefix.
					const foreignAmounts = (contractData.additionalAmounts ?? []).filter(
						(extra) => extra.currency !== contractData.currencyLocal
					);
					const showWorkPlanCurrency =
						!!contractData.workPlanCurrency &&
						contractData.workPlanCurrency !== contractData.currency;
					if (foreignAmounts.length === 0 && !showWorkPlanCurrency) return null;
					return (
						<Box>
							{foreignAmounts.map((extra) => (
								<Typography
									key={extra.currency}
									sx={{
										color: 'text.secondary',
										fontSize: '1.1rem',
										fontWeight: 500,
									}}>
									+ {formatCurrency({ currency: extra.currency, value: extra.amount })}
								</Typography>
							))}
							{showWorkPlanCurrency && (
								<Typography
									sx={{
										color: 'text.secondary',
										fontSize: '1.1rem',
										fontStyle: 'italic',
									}}>
									{t('workPlanCurrency', { currency: contractData.workPlanCurrency })}
								</Typography>
							)}
						</Box>
					);
				})()}
				{hasCommitment && (
					<StatItem
						label={t('commitment')}
						tooltip={TOOLTIPS.commitmentAmount}
						value={formatCurrency({
							currency: contractData.currency,
							value: commitmentAmount,
						})}
						secondary={localCaptionContract(commitmentAmount)}
						color={theme.palette.primary.main}
					/>
				)}
				<StatItem
					label={t('paid')}
					tooltip={(() => {
						// When the contract has a per-loan breakdown with more than
						// one source, render a compact table inside the tooltip so
						// the user sees how the headline paid figure splits across
						// project loans. Single-source (or no breakdown) keeps the
						// generic blurb.
						const breakdown = contractData.paidByLoan ?? [];
						if (breakdown.length <= 1) return TOOLTIPS.paid;
						const totalForPct = breakdown.reduce((t, b) => t + (b.amount || 0), 0);
						return (
							<Box sx={{ minWidth: 240 }}>
								<Typography variant="caption" sx={{ display: 'block', mb: 0.5, opacity: 0.85 }}>
									{TOOLTIPS.paid}
								</Typography>
								<Stack spacing={0.25}>
									{breakdown.map((b) => {
										const pct = totalForPct > 0 ? (b.amount / totalForPct) * 100 : 0;
										return (
											<Stack
												key={b.loanId}
												direction="row"
												spacing={1}
												justifyContent="space-between">
												<Typography variant="caption" sx={{ flex: 1 }}>
													{b.loanName || `Loan #${b.loanId}`}
												</Typography>
												<Typography variant="caption">
													{formatCurrency({
														currency: contractData.currency,
														value: b.amount,
													})}
												</Typography>
												<Typography variant="caption" sx={{ minWidth: 44, textAlign: 'right' }}>
													{`${pct.toFixed(0)}%`}
												</Typography>
											</Stack>
										);
									})}
								</Stack>
							</Box>
						);
					})()}
					value={formatCurrency({
						currency: contractData.currency,
						value: contractData.paid,
					})}
					secondary={localCaptionContract(contractData.paid)}
					color={theme.palette.success.main}
				/>
				{paidV1Amount > 0 && (
					<StatItem
						label="Legacy (V1)"
						tooltip={TOOLTIPS.paidV1}
						value={formatCurrency({
							currency: contractData.currency,
							value: paidV1Amount,
						})}
						secondary={localCaptionContract(paidV1Amount)}
						color={alpha(theme.palette.success.main, 0.7)}
					/>
				)}
				{pendingAmount > 0 && (
					<StatItem
						label={t('pending')}
						tooltip={TOOLTIPS.pending}
						value={formatCurrency({
							currency: contractData.currency,
							value: pendingAmount,
						})}
						secondary={localCaptionContract(pendingAmount)}
						color={theme.palette.warning.main}
					/>
				)}
				<StatItem
					label={t('remaining')}
					tooltip={TOOLTIPS.remaining}
					value={formatCurrency({
						currency: contractData.currency,
						value: remainingAmount,
					})}
					secondary={localCaptionContract(remainingAmount)}
					color={theme.palette.info.main}
				/>
			</Stack>
		);
	};

	// Render work plan/activity variant
	const renderWorkPlanActivityRow = () => {
		if (!data) return null;

		return (
			<Stack
				direction="row"
				alignItems="center"
				spacing={{ xs: 2, md: 4 }}
				sx={{ flexWrap: 'wrap', gap: { xs: 1.5, md: 2 } }}>
				{/* Progress bar with label - matching stat item layout */}
				<Box sx={{ minWidth: 280 }}>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{ mb: 0.5 }}>
						<Typography
							sx={{
								color: '#6b7280',
								fontSize: { xs: '1.125rem', sm: '1.25rem' },
								fontWeight: 600,
								letterSpacing: '0.03em',
								textTransform: 'uppercase',
							}}>
							{t('utilization')}
						</Typography>
						<Tooltip
							title={`${spentPercent}% spent, ${commitmentPercent}% committed of planned budget`}
							arrow
							placement="top">
							<Typography
								sx={{
									color:
										utilizationPercent >= 70
											? theme.palette.success.main
											: utilizationPercent >= 30
												? theme.palette.warning.main
												: theme.palette.text.secondary,
									fontSize: { xs: '1.3125rem', sm: '1.5rem' },
									fontWeight: 600,
									cursor: 'help',
								}}>
								{utilizationPercent}%
							</Typography>
						</Tooltip>
					</Stack>
					<Box
						sx={{
							bgcolor: alpha(theme.palette.grey[300], 0.4),
							borderRadius: 0.5,
							display: 'flex',
							height: 10,
							overflow: 'hidden',
						}}>
						<ProgressSegment
							color={theme.palette.success.main}
							onMouseEnter={(e) =>
								handlePopoverOpen(e, {
									color: 'success.main',
									label: t('spendPlan.workPlan.spentRedeemedPct', { pct: spentPercent }),
									value: formatCurrency({ currency: data.currency, value: data.spent }),
								})
							}
							onMouseLeave={handlePopoverClose}
							width={`${spentPercent}%`}
						/>
						<ProgressSegment
							color={alpha(theme.palette.warning.main, 0.6)}
							onMouseEnter={(e) =>
								handlePopoverOpen(e, {
									color: 'warning.main',
									label: t('spendPlan.workPlan.committedPct', { pct: Math.max(0, commitmentPercent - spentPercent) }),
									value: formatCurrency({
										currency: data.currency,
										value: data.committed - data.spent,
									}),
								})
							}
							onMouseLeave={handlePopoverClose}
							width={`${Math.max(0, commitmentPercent - spentPercent)}%`}
						/>
						<ProgressSegment
							color={alpha(theme.palette.info.main, 0.3)}
							onMouseEnter={(e) =>
								handlePopoverOpen(e, {
									color: 'info.main',
									label: t('spendPlan.workPlan.availableToPlanPct', { pct: Math.max(0, 100 - commitmentPercent) }),
									value: formatCurrency({ currency: data.currency, value: availableAmount }),
								})
							}
							onMouseLeave={handlePopoverClose}
							width={`${Math.max(0, 100 - commitmentPercent)}%`}
						/>
					</Box>
				</Box>

				{/* Stats */}
				<StatItem
					label={variant === 'workPlan' ? t('spendPlan.workPlanAmount') : t('planned')}
					tooltip={variant === 'workPlan' ? TOOLTIPS.workPlanAmount : TOOLTIPS.planned}
					value={formatCurrency({ currency: data.currency, value: data.planned })}
					secondary={localCaption(data.planned)}
				/>
				<StatItem
					label={variant === 'workPlan' ? t('planned') : t('committed')}
					tooltip={variant === 'workPlan' ? TOOLTIPS.plannedWorkPlan : TOOLTIPS.committed}
					value={formatCurrency({ currency: data.currency, value: data.committed })}
					secondary={localCaption(data.committed)}
					color={theme.palette.warning.main}
				/>
				<StatItem
					label={t('spendPlan.workPlan.spentRedeemed')}
					tooltip={TOOLTIPS.spent}
					value={formatCurrency({ currency: data.currency, value: data.spent })}
					secondary={localCaption(data.spent)}
					color={theme.palette.success.main}
				/>
				<StatItem
					label={t('spendPlan.availableToPlan')}
					tooltip={TOOLTIPS.available}
					value={formatCurrency({ currency: data.currency, value: availableAmount })}
					secondary={localCaption(availableAmount)}
					color={availableAmount < 0 ? theme.palette.error.main : theme.palette.info.main}
				/>

				{/* Sub-activity allocation (activity variant only) */}
				{variant === 'activity' && subActivityAllocation && (
					<>
						<StatItem
							label={t('allocated')}
							tooltip={TOOLTIPS.allocated}
							value={formatCurrency({
								currency: data.currency,
								value: subActivityAllocation.allocated,
							})}
							color={theme.palette.info.main}
						/>
						<StatItem
							label={t('unallocated')}
							tooltip={t('spendPlan.workPlan.unallocatedTooltip')}
							value={formatCurrency({
								currency: data.currency,
								value: subActivityAllocation.unallocated,
							})}
							color={subActivityAllocation.unallocated < 0 ? theme.palette.error.main : undefined}
						/>
					</>
				)}
			</Stack>
		);
	};

	return (
		<>
			<Box
				sx={{
					bgcolor: '#ffffff',
					borderTop: '1px solid',
					borderColor: 'divider',
					px: { xs: 2, sm: 3 },
					py: 1.5,
					width: '100%',
				}}>
				{variant === 'contract' ? renderContractRow() : renderWorkPlanActivityRow()}
			</Box>

			{/* Popover for progress bar hover */}
			<Popover
				anchorEl={anchorEl}
				anchorOrigin={{
					horizontal: 'center',
					vertical: 'top',
				}}
				disableRestoreFocus
				open={popoverOpen}
				sx={{
					pointerEvents: 'none',
				}}
				transformOrigin={{
					horizontal: 'center',
					vertical: 'bottom',
				}}>
				{popoverContent && (
					<Box sx={{ p: 2 }}>
						<Typography
							sx={{
								color: 'text.secondary',
								fontSize: { xs: '1rem', sm: '1.125rem' },
								mb: 0.5,
							}}>
							{popoverContent.label}
						</Typography>
						<Typography
							sx={{
								color: popoverContent.color,
								fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
								fontWeight: 600,
							}}>
							{popoverContent.value}
						</Typography>
					</Box>
				)}
			</Popover>
		</>
	);
};

export default RowFinancialSummary;
